import { useState, useEffect, useCallback } from 'react';
import { OrderTable } from '../../components/orders';
import orderService from '../../services/orderService';
import './OrderListPage.css';

function OrderListPage() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0, // Spring Data uses 0-indexed pages
    size: 10,
    total: 0,
    totalPages: 0,
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
    cancelled: 0,
    failed: 0,
  });

  // Filters
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(''); // Combined filter: 'status:pending', 'status:cancelled', 'payment:paid'
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Parse combined filter into status and paymentStatus
  const getFilterParams = () => {
    if (!filter) return { status: '', paymentStatus: '' };
    const [type, value] = filter.split(':');
    if (type === 'status') return { status: value, paymentStatus: '' };
    if (type === 'payment') return { status: '', paymentStatus: value };
    return { status: '', paymentStatus: '' };
  };

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const { status, paymentStatus } = getFilterParams();

    try {
      const response = await orderService.getOrders({
        page: pagination.page,
        size: pagination.size,
        sort: `${sortBy},${sortOrder}`,
        search,
        status,
        paymentStatus,
      });

      // Handle ApiResponse wrapper with Spring Data Page format
      const responseData = response.data?.data || response.data;
      const ordersData = responseData?.content || responseData || [];
      const totalElements = responseData?.totalElements ?? responseData?.total ?? 0;
      const totalPages = responseData?.totalPages ?? Math.ceil(totalElements / pagination.size);

      setOrders(ordersData);
      setPagination((prev) => ({
        ...prev,
        total: totalElements,
        totalPages: totalPages,
      }));

      // Extract stats from API response (backend now provides aggregate stats)
      const apiStats = responseData?.stats;
      if (apiStats) {
        setStats({
          total: apiStats.totalOrders ?? totalElements,
          pending: apiStats.pendingOrders ?? 0,
          paid: apiStats.paidOrders ?? 0,
          cancelled: apiStats.cancelledOrders ?? 0,
          failed: apiStats.failedOrders ?? 0,
        });
      } else {
        // Fallback: use totalElements for total, zeros for others
        setStats((prev) => ({
          ...prev,
          total: totalElements,
        }));
      }
    } catch (err) {
      setError('Failed to load orders');
      console.error('Error fetching orders:', err);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.size, search, filter, sortBy, sortOrder]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, page: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSort = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleFilterChange = (value) => {
    setFilter(value);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setSearch('');
    setFilter('');
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const hasActiveFilters = search || filter;

  return (
    <div className="order-list-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Orders</h1>
          <p>Manage customer orders, update statuses, and process refunds.</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="quick-stats">
        <div className="stat-card">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total Orders</span>
        </div>
        <div className="stat-card stat-warning">
          <span className="stat-value">{stats.pending}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat-card stat-success">
          <span className="stat-value">{stats.paid}</span>
          <span className="stat-label">Paid</span>
        </div>
        <div className="stat-card stat-danger">
          <span className="stat-value">{stats.cancelled}</span>
          <span className="stat-label">Cancelled</span>
        </div>
        <div className="stat-card stat-danger">
          <span className="stat-value">{stats.failed}</span>
          <span className="stat-label">Failed</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Search orders, customers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <select
            value={filter}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="payment:pending">Pending</option>
            <option value="status:cancelled">Cancelled</option>
            <option value="payment:paid">Paid</option>
            <option value="payment:failed">Failed</option>
          </select>

          {hasActiveFilters && (
            <button className="btn btn-text" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={fetchOrders}>Try again</button>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="table-loading">
          <div className="loading-spinner"></div>
          <span>Loading orders...</span>
        </div>
      ) : (
        <>
          <OrderTable
            orders={orders}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {pagination.page * pagination.size + 1} to{' '}
                {Math.min((pagination.page + 1) * pagination.size, pagination.total)} of{' '}
                {pagination.total} orders
              </div>
              <div className="pagination-controls">
                <button
                  className="btn-page"
                  disabled={pagination.page === 0}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                  Previous
                </button>
                <div className="page-numbers">
                  {Array.from({ length: pagination.totalPages }, (_, i) => i).map((pageIndex) => (
                    <button
                      key={pageIndex}
                      className={`btn-page-number ${pagination.page === pageIndex ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageIndex)}
                    >
                      {pageIndex + 1}
                    </button>
                  ))}
                </div>
                <button
                  className="btn-page"
                  disabled={pagination.page >= pagination.totalPages - 1}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Next
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default OrderListPage;
